import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Plus, Pencil, Trash, Waves, CalendarDays, MapPin, Clock3, Users2,
  Copy, ArrowUp, ArrowDown, List, FileText, Paperclip, Download, X,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Check, BookOpen, UserPlus, UserMinus, Save,
  FileSpreadsheet, Upload, ClipboardCheck, Clock, CheckCircle, Bell
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import AttendanceDialog from '../training/AttendanceDialog';
import TemplateManagerDialog from '../training/TemplateManagerDialog';
import ImportSessionsDialog from '../training/ImportSessionsDialog';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const INITIAL_SESSION = {
  name: '',
  discipline: 'MIX',
  infrastructure_id: '',
  coach_id: '',
  coach_name: '',
  location: {
    building_name: '',
    organization: '',
    street_address: '',
    city: '',
    postal_code: '',
    country: ''
  },
  pool_specs: {
    type: '25m',
    length_meters: 25,
    number_of_lanes: 6,
    depth_shallow_end: 1.2,
    depth_deep_end: 1.8,
    max_participants: 12
  },
  season: 'Spring',
  training_date: '',
  start_time: '19:00',
  end_time: '20:30',
  exercise_blocks: [],
  notes: '',
  is_template: false,
  template_name: '',
  attachments: [],
  participants: []
};

const TrainingSessionsTab = ({
  trainingSessions,
  trainingTemplates,
  customTemplates = [],
  coaches,
  freedivers = [],
  aquaticInfrastructures,
  onRefresh,
  t,
  language
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [form, setForm] = useState({ ...INITIAL_SESSION });
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState(new Set());
  const attachmentInputRef = useRef(null);

  // Inline editing state
  const [inlineEditing, setInlineEditing] = useState(null); // session ID being inline-edited
  const [inlineForm, setInlineForm] = useState({});

  // Template management state
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', discipline: 'MIX', duration_minutes: 90, description: '', blocks: [], total_distance: 0, notes: '' });

  // Participant management state
  const [showParticipantDialog, setShowParticipantDialog] = useState(null); // session ID

  // Attendance tracking state
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(null); // session ID
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  // Import dialog state (Excel/CSV + PDF AI) — extracted to ImportSessionsDialog
  const [showExerciseImportDialog, setShowExerciseImportDialog] = useState(false);

  // Delete confirmation state
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);

  const translate = (key, fallback) => t ? t(key) : fallback;

  // Month navigation helpers
  const navigateMonth = (direction) => {
    const current = calendarMonth || new Date().toISOString().slice(0, 7);
    const [year, month] = current.split('-').map(Number);
    const d = new Date(year, month - 1 + direction, 1);
    setCalendarMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const getMonthLabel = () => {
    const current = calendarMonth || new Date().toISOString().slice(0, 7);
    const [year, month] = current.split('-').map(Number);
    const d = new Date(year, month - 1, 1);
    return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { month: 'long', year: 'numeric' });
  };

  // Calculate capacity status for a session
  const getCapacityStatus = (session) => {
    const currentParticipants = session.participants?.length || 0;
    const maxParticipants = session.pool_specs?.max_participants || 0;
    
    if (maxParticipants === 0) return { status: 'unknown', percentage: 0, label: '-', color: 'slate' };
    
    const percentage = Math.round((currentParticipants / maxParticipants) * 100);
    
    if (currentParticipants >= maxParticipants) {
      return { status: 'full', percentage: 100, label: translate('full', 'Full'), color: 'red' };
    } else if (percentage >= 80) {
      return { status: 'nearFull', percentage, label: translate('nearFull', 'Near Full'), color: 'orange' };
    } else if (percentage >= 50) {
      return { status: 'filling', percentage, label: translate('filling', 'Filling'), color: 'yellow' };
    } else {
      return { status: 'available', percentage, label: translate('available', 'Available'), color: 'green' };
    }
  };

  const resetForm = () => {
    setForm({ ...INITIAL_SESSION });
  };

  const createSessionFromTemplate = async (templateId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/training-sessions/from-template?template_id=${templateId}`, form);
      alert('Training session created from template successfully!');
      await onRefresh();
      setShowForm(false);
      setSelectedTemplate('');
      resetForm();
    } catch (error) {
      console.error('Error creating training session from template:', error);
      alert('Error creating training session from template. Please try again.');
    }
  };

  const createSession = async () => {
    try {
      if (selectedTemplate && selectedTemplate !== 'custom' && selectedTemplate !== '') {
        await createSessionFromTemplate(selectedTemplate);
      } else {
        const sessionData = {
          ...form,
          training_date: new Date(form.training_date).toISOString(),
          exercise_blocks: form.exercise_blocks || []
        };
        await axios.post(`${API_BASE_URL}/api/training-sessions`, sessionData);
        alert('Training session created successfully!');
        await onRefresh();
        setShowForm(false);
        setSelectedTemplate('');
        resetForm();
      }
    } catch (error) {
      console.error('Error creating training session:', error);
      alert('Error creating training session. Please try again.');
    }
  };

  const updateSession = async () => {
    try {
      const { infrastructure_id, coach_id, ...validFields } = form;
      const sessionData = {
        ...validFields,
        training_date: new Date(form.training_date).toISOString(),
        exercise_blocks: form.exercise_blocks || []
      };
      await axios.put(`${API_BASE_URL}/api/training-sessions/${editing.id}`, sessionData);
      setShowForm(false);
      setEditing(null);
      setSelectedTemplate('');
      resetForm();
      await onRefresh();
    } catch (error) {
      console.error('Error updating training session:', error);
      alert(translate('error', 'Error') + ': ' + (error.response?.data?.detail || 'Error updating training session. Please try again.'));
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/training-sessions/${sessionId}`);
      setConfirmingDeleteId(null);
      await onRefresh();
    } catch (error) {
      console.error('Error deleting training session:', error);
    }
  };

  const openEdit = (session) => {
    setEditing(session);
    setForm({
      name: session.name,
      discipline: session.discipline,
      location: session.location,
      pool_specs: session.pool_specs,
      season: session.season,
      training_date: session.training_date.split('T')[0],
      start_time: session.start_time,
      end_time: session.end_time,
      exercise_blocks: session.exercise_blocks || [],
      coach_name: session.coach_name || '',
      notes: session.notes || '',
      is_template: session.is_template || false,
      template_name: session.template_name || '',
      attachments: session.attachments || [],
      participants: session.participants || []
    });
    setShowForm(true);
    setSelectedTemplate('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cloneSession = async (sessionId, newDate, newName) => {
    try {
      await axios.post(`${API_BASE_URL}/api/training-sessions/${sessionId}/clone`, {
        new_date: new Date(newDate).toISOString(),
        new_name: newName
      });
      alert('Training session cloned successfully!');
      await onRefresh();
    } catch (error) {
      console.error('Error cloning training session:', error);
      alert('Error cloning training session. Please try again.');
    }
  };

  const handleAttachmentUpload = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/pdf'
    ];
    const maxSize = 10 * 1024 * 1024;
    const newAttachments = [...(form.attachments || [])];
    Array.from(files).forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name}: ${translate('invalidFileType', 'Invalid file type')}. ${translate('allowedFileTypes', 'Allowed: .xlsx, .xls, .docx, .doc, .pdf')}`);
        return;
      }
      if (file.size > maxSize) {
        alert(`${file.name}: ${translate('fileTooLarge', 'File too large')} (max 10MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const attachment = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          filename: file.name,
          file_url: reader.result,
          file_type: file.type,
          file_size: file.size,
          upload_date: new Date().toISOString()
        };
        newAttachments.push(attachment);
        setForm(prev => ({ ...prev, attachments: [...newAttachments] }));
      };
      reader.readAsDataURL(file);
    });
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
  };

  const removeAttachment = (attachmentId) => {
    setForm(prev => ({
      ...prev,
      attachments: (prev.attachments || []).filter(a => a.id !== attachmentId)
    }));
  };

  const toggleExpanded = (sessionId) => {
    const newExpanded = new Set(expandedDetails);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedDetails(newExpanded);
  };

  // ========== INLINE EDITING ==========
  const startInlineEdit = (session) => {
    setInlineEditing(session.id);
    setInlineForm({
      name: session.name,
      discipline: session.discipline,
      training_date: session.training_date?.split('T')[0] || '',
      start_time: session.start_time || '',
      end_time: session.end_time || ''
    });
  };

  const saveInlineEdit = async (sessionId) => {
    try {
      const updateData = {
        name: inlineForm.name,
        discipline: inlineForm.discipline,
        training_date: new Date(inlineForm.training_date).toISOString(),
        start_time: inlineForm.start_time,
        end_time: inlineForm.end_time
      };
      await axios.put(`${API_BASE_URL}/api/training-sessions/${sessionId}`, updateData);
      setInlineEditing(null);
      setInlineForm({});
      await onRefresh();
    } catch (error) {
      console.error('Error saving inline edit:', error);
      alert(translate('error', 'Error') + ': ' + (error.response?.data?.detail || 'Could not save changes'));
    }
  };

  const cancelInlineEdit = () => {
    setInlineEditing(null);
    setInlineForm({});
  };

  // ========== TEMPLATE MANAGEMENT ==========
  const resetTemplateForm = () => {
    setTemplateForm({ name: '', discipline: 'MIX', duration_minutes: 90, description: '', blocks: [], total_distance: 0, notes: '' });
    setEditingTemplate(null);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      discipline: template.discipline,
      duration_minutes: template.duration_minutes,
      description: template.description || '',
      blocks: template.blocks || [],
      total_distance: template.total_distance || 0,
      notes: template.notes || ''
    });
    setShowTemplateManager(true);
  };

  const saveTemplate = async () => {
    try {
      if (editingTemplate) {
        await axios.put(`${API_BASE_URL}/api/custom-templates/${editingTemplate.id}`, templateForm);
      } else {
        await axios.post(`${API_BASE_URL}/api/custom-templates`, templateForm);
      }
      resetTemplateForm();
      setShowTemplateManager(false);
      await onRefresh();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm(translate('confirmDelete', 'Are you sure you want to delete this template?'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/custom-templates/${templateId}`);
      await onRefresh();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template');
    }
  };

  // ========== PARTICIPANT MANAGEMENT ==========
  const addParticipant = async (sessionId, freediverId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/training-sessions/${sessionId}/participants`, { freediver_id: freediverId });
      await onRefresh();
    } catch (error) {
      console.error('Error adding participant:', error);
      alert(error.response?.data?.detail || 'Error adding participant');
    }
  };

  const removeParticipant = async (sessionId, freediverId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/training-sessions/${sessionId}/participants/${freediverId}`);
      await onRefresh();
    } catch (error) {
      console.error('Error removing participant:', error);
      alert('Error removing participant');
    }
  };

  const getFreediverName = (id) => {
    const f = freedivers.find(fd => fd.id === id);
    return f ? `${f.first_name} ${f.last_name}` : id;
  };

  // Attendance management functions
  const loadAttendance = async (sessionId) => {
    try {
      setAttendanceLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/training-sessions/${sessionId}/attendance`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error('Error loading attendance:', error);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const updateAttendance = async (sessionId, freediverId, status) => {
    try {
      await axios.put(`${API_BASE_URL}/api/training-sessions/${sessionId}/attendance`, {
        [freediverId]: status
      });
      // Reload attendance data
      loadAttendance(sessionId);
    } catch (error) {
      console.error('Error updating attendance:', error);
      alert('Error updating attendance');
    }
  };

  const openAttendanceDialog = (sessionId) => {
    setShowAttendanceDialog(sessionId);
    loadAttendance(sessionId);
  };

  // Exercise import functions
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200">{translate('trainingSessionsTitle', 'Training Sessions')}</h2>
        <div className="flex space-x-4">
          <div className="flex space-x-2">
            <Button
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? "default" : "outline"}
              size="sm"
              className="bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="training-list-view-btn"
            >
              <List className="mr-2" size={16} />
              {translate('listView', 'List')}
            </Button>
            <Button
              onClick={() => setViewMode('calendar')}
              variant={viewMode === 'calendar' ? "default" : "outline"}
              size="sm"
              className="bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="training-calendar-view-btn"
            >
              <CalendarDays className="mr-2" size={16} />
              {translate('calendarView', 'Calendar')}
            </Button>
          </div>
          <Button
            onClick={() => setShowExerciseImportDialog(true)}
            variant="outline"
            className="border-green-500 text-green-200 hover:bg-green-700/50"
            data-testid="import-exercises-btn"
          >
            <FileSpreadsheet className="mr-2" size={16} />
            {translate('importSessions', 'Import Sessions')}
          </Button>
          <Button
            onClick={() => { resetTemplateForm(); setShowTemplateManager(true); }}
            variant="outline"
            className="border-purple-500 text-purple-200 hover:bg-purple-700/50"
            data-testid="manage-templates-btn"
          >
            <BookOpen className="mr-2" size={16} />
            {translate('manageTemplates', 'Templates')}
          </Button>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white border-none"
            data-testid="new-training-session-btn"
          >
            <Plus className="mr-2" size={18} />
            {translate('newTrainingSession', 'New Training Session')}
          </Button>
        </div>
      </div>

      {/* Exercise Import Dialog (Excel/CSV + PDF AI) */}
      <ImportSessionsDialog
        open={showExerciseImportDialog}
        onClose={() => setShowExerciseImportDialog(false)}
        onComplete={onRefresh}
        translate={translate}
      />

      {/* Attendance Dialog */}
      <AttendanceDialog
        showAttendanceDialog={showAttendanceDialog}
        setShowAttendanceDialog={setShowAttendanceDialog}
        attendanceData={attendanceData}
        attendanceLoading={attendanceLoading}
        updateAttendance={updateAttendance}
        translate={translate}
      />

      {/* Template Manager Dialog */}
      <TemplateManagerDialog
        showTemplateManager={showTemplateManager}
        setShowTemplateManager={setShowTemplateManager}
        customTemplates={customTemplates}
        editingTemplate={editingTemplate}
        templateForm={templateForm}
        setTemplateForm={setTemplateForm}
        resetTemplateForm={resetTemplateForm}
        openEditTemplate={openEditTemplate}
        deleteTemplate={deleteTemplate}
        saveTemplate={saveTemplate}
        translate={translate}
      />

      {/* Participant Management Dialog */}
      <Dialog open={!!showParticipantDialog} onOpenChange={(open) => { if (!open) setShowParticipantDialog(null); }}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-teal-700 text-white max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-teal-200 flex items-center">
              <Users2 className="mr-2" size={20} />
              {translate('manageParticipants', 'Manage Participants')}
            </DialogTitle>
          </DialogHeader>
          {showParticipantDialog && (() => {
            const session = trainingSessions.find(s => s.id === showParticipantDialog);
            if (!session) return <p className="text-blue-300">Session not found</p>;
            const participants = session.participants || [];
            const available = freedivers.filter(f => !participants.includes(f.id));
            return (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-teal-300 mb-2">{translate('currentParticipants', 'Current Participants')} ({participants.length})</h4>
                  {participants.length === 0 ? (
                    <p className="text-blue-400 text-sm italic">{translate('noParticipants', 'No participants assigned')}</p>
                  ) : (
                    <div className="space-y-1">
                      {participants.map(pid => (
                        <div key={pid} className="flex items-center justify-between p-2 bg-teal-800/30 rounded">
                          <span className="text-teal-100 text-sm">{getFreediverName(pid)}</span>
                          <Button size="sm" variant="ghost" className="text-red-300 hover:bg-red-700/50 h-7 w-7 p-0" onClick={() => removeParticipant(session.id, pid)} data-testid={`remove-participant-${pid}`}>
                            <UserMinus size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <hr className="border-teal-700/50" />
                <div>
                  <h4 className="text-sm font-semibold text-teal-300 mb-2">{translate('addParticipant', 'Add Participant')}</h4>
                  {available.length === 0 ? (
                    <p className="text-blue-400 text-sm italic">{translate('allFreediversAssigned', 'All freedivers are already assigned')}</p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {available.map(f => (
                        <div key={f.id} className="flex items-center justify-between p-2 bg-blue-800/30 rounded hover:bg-blue-700/30 transition-colors">
                          <span className="text-blue-100 text-sm">{f.first_name} {f.last_name}</span>
                          <Button size="sm" variant="ghost" className="text-green-300 hover:bg-green-700/50 h-7 w-7 p-0" onClick={() => addParticipant(session.id, f.id)} data-testid={`add-participant-${f.id}`}>
                            <UserPlus size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Training Session Creation Form */}
      {showForm && (
        <Card className="bg-gradient-to-br from-blue-800/40 to-purple-800/40 border-blue-700/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-blue-200 flex items-center">
              {editing ? <Pencil className="mr-2" size={20} /> : <Plus className="mr-2" size={20} />}
              {editing ? translate('editTrainingSession', 'Edit Training Session') : translate('createNewTrainingSession', 'Create New Training Session')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-blue-200 text-sm font-semibold">Session Name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="e.g., Advanced Dynamic Training"
                  className="bg-blue-800/50 border-blue-600 text-white mt-1"
                  data-testid="session-name-input"
                />
              </div>
              <div>
                <label className="text-blue-200 text-sm font-semibold">Discipline</label>
                <Select 
                  value={form.discipline} 
                  onValueChange={(value) => setForm({...form, discipline: value})}
                >
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1">
                    <SelectValue placeholder="Select discipline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STA">Static Apnea (STA)</SelectItem>
                    <SelectItem value="DYN">Dynamic Apnea (DYN)</SelectItem>
                    <SelectItem value="DYNBF">Dynamic Bifins (DYNBF)</SelectItem>
                    <SelectItem value="MIX">Mixed Training</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-blue-200 text-sm font-semibold">{translate('coachInstructorName', 'Coach/Instructor')}</label>
                <Select 
                  value={form.coach_id || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setForm({...form, coach_id: '', coach_name: ''});
                    } else {
                      const coach = coaches.find(c => c.id === value);
                      setForm({
                        ...form, 
                        coach_id: value,
                        coach_name: coach ? `${coach.first_name} ${coach.last_name}` : ''
                      });
                    }
                  }}
                >
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1">
                    <SelectValue placeholder={translate('selectCoach', 'Select a coach...')} />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600">
                    <SelectItem value="none">{translate('noCoachSelected', 'No coach selected')}</SelectItem>
                    {coaches.map((coach) => (
                      <SelectItem key={coach.id} value={coach.id}>
                        {coach.first_name} {coach.last_name} ({coach.nationality})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {coaches.length === 0 && (
                  <p className="text-xs text-blue-400 mt-1">
                    {translate('noCoachesAvailable', 'No coaches available. Mark a freediver as "Coach/Trainer" in the Freedivers section.')}
                  </p>
                )}
              </div>
            </div>

            {/* Template Selection - Only show when creating new session */}
            {!editing && (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-blue-200">{translate('trainingTemplate', 'Training Template (Optional)')}</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-blue-200 text-sm font-semibold">{translate('choosePrebuiltTemplates', 'Choose from Pre-built Templates')}</label>
                    <Select 
                      value={selectedTemplate} 
                      onValueChange={(value) => setSelectedTemplate(value)}
                    >
                      <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1">
                        <SelectValue placeholder="Select a training template or create custom" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Create Custom Session</SelectItem>
                        {customTemplates.length > 0 && customTemplates.map((ct) => (
                          <SelectItem key={`custom-${ct.id}`} value={`custom-${ct.id}`}>
                            {ct.name} - {ct.discipline} ({ct.duration_minutes} min) [Custom]
                          </SelectItem>
                        ))}
                        {Object.entries(trainingTemplates).map(([key, template]) => (
                          <SelectItem key={key} value={key}>
                            {template.name} - {template.discipline} ({template.duration_minutes} min)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedTemplate && (trainingTemplates[selectedTemplate] || customTemplates.find(ct => `custom-${ct.id}` === selectedTemplate)) && (() => {
                    const isCustom = selectedTemplate.startsWith('custom-');
                    const tmpl = isCustom 
                      ? customTemplates.find(ct => `custom-${ct.id}` === selectedTemplate) 
                      : trainingTemplates[selectedTemplate];
                    if (!tmpl) return null;
                    return (
                      <div className="bg-blue-800/30 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="text-blue-200 font-semibold">{tmpl.name}</h5>
                          {isCustom && <Badge className="bg-purple-600 text-xs">Custom</Badge>}
                        </div>
                        <p className="text-blue-300 text-sm mb-3">{tmpl.description}</p>
                        <div className="space-y-2">
                          <p className="text-blue-300 text-sm font-semibold">Training Blocks:</p>
                          {tmpl.blocks?.map((block, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-blue-200">{block.name}</span>
                              <span className="text-blue-300">{block.duration_minutes} min</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 flex justify-between text-sm">
                          <span className="text-blue-200">Total Distance:</span>
                          <span className="text-blue-300">{tmpl.total_distance}m</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Location */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-blue-200">Training Location</h4>
              
              {/* Pool Location Selector from Aquatic Infrastructure */}
              {aquaticInfrastructures.length > 0 && (
                <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-700/30">
                  <label className="text-blue-200 text-sm font-semibold">Quick Fill from Saved Pool Locations</label>
                  <Select 
                    value={form.infrastructure_id || 'manual'}
                    onValueChange={(value) => {
                      if (value === 'manual') {
                        setForm({
                          ...form,
                          infrastructure_id: '',
                          location: { building_name: '', organization: '', street_address: '', city: '', postal_code: '', country: '' },
                          pool_specs: { type: '25m', length_meters: 25, number_of_lanes: 6, depth_shallow_end: 1.2, depth_deep_end: 3.0 }
                        });
                      } else {
                        const selectedInfra = aquaticInfrastructures.find(i => i.id === value);
                        if (selectedInfra) {
                          setForm({
                            ...form,
                            infrastructure_id: value,
                            location: {
                              building_name: selectedInfra.building_name || selectedInfra.long_name || '',
                              organization: selectedInfra.organization || '',
                              street_address: `${selectedInfra.address_number || ''} ${selectedInfra.address_street || ''} ${selectedInfra.address_type || ''}`.trim(),
                              city: selectedInfra.city || '',
                              postal_code: selectedInfra.postal_code || '',
                              country: selectedInfra.country || ''
                            },
                            pool_specs: {
                              type: selectedInfra.pool_type || '25m',
                              length_meters: selectedInfra.pool_length || 25,
                              number_of_lanes: selectedInfra.number_of_lanes || 6,
                              depth_shallow_end: selectedInfra.min_depth || 1.2,
                              depth_deep_end: selectedInfra.max_depth || 3.0
                            }
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1">
                      <SelectValue placeholder="Select a pool location or enter manually" />
                    </SelectTrigger>
                    <SelectContent className="bg-blue-800 border-blue-600">
                      <SelectItem value="manual">Enter Manually</SelectItem>
                      {aquaticInfrastructures.map((infra) => (
                        <SelectItem key={infra.id} value={infra.id}>
                          {infra.short_name} - {infra.pool_type} ({infra.city})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-blue-400 text-xs mt-1">
                    Select a saved pool location to auto-fill the details below, or choose &ldquo;Enter Manually&rdquo; to fill in custom values.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  value={form.location.building_name}
                  onChange={(e) => setForm({...form, location: {...form.location, building_name: e.target.value}})}
                  placeholder="Building/Facility Name"
                  className="bg-blue-800/50 border-blue-600 text-white"
                />
                <Input
                  value={form.location.organization}
                  onChange={(e) => setForm({...form, location: {...form.location, organization: e.target.value}})}
                  placeholder="Host Organization"
                  className="bg-blue-800/50 border-blue-600 text-white"
                />
              </div>
              <Input
                value={form.location.street_address}
                onChange={(e) => setForm({...form, location: {...form.location, street_address: e.target.value}})}
                placeholder="Street Address"
                className="bg-blue-800/50 border-blue-600 text-white"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  value={form.location.city}
                  onChange={(e) => setForm({...form, location: {...form.location, city: e.target.value}})}
                  placeholder="City"
                  className="bg-blue-800/50 border-blue-600 text-white"
                />
                <Input
                  value={form.location.postal_code}
                  onChange={(e) => setForm({...form, location: {...form.location, postal_code: e.target.value}})}
                  placeholder="Postal Code"
                  className="bg-blue-800/50 border-blue-600 text-white"
                />
                <Input
                  value={form.location.country}
                  onChange={(e) => setForm({...form, location: {...form.location, country: e.target.value}})}
                  placeholder="Country"
                  className="bg-blue-800/50 border-blue-600 text-white"
                />
              </div>
            </div>

            {/* Pool Specifications */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-blue-200">Pool Specifications</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-blue-200 text-sm">Pool Type</label>
                  <Select 
                    value={form.pool_specs.type} 
                    onValueChange={(value) => {
                      const length = value === '25m' ? 25 : value === '50m' ? 50 : value === '33m' ? 33 : 25;
                      setForm({...form, pool_specs: {...form.pool_specs, type: value, length_meters: length}});
                    }}
                  >
                    <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25m">25m Pool</SelectItem>
                      <SelectItem value="50m">50m Pool</SelectItem>
                      <SelectItem value="33m">33m Pool</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-blue-200 text-sm">Number of Lanes</label>
                  <Input
                    type="number"
                    value={form.pool_specs.number_of_lanes}
                    onChange={(e) => setForm({...form, pool_specs: {...form.pool_specs, number_of_lanes: parseInt(e.target.value) || 6}})}
                    className="bg-blue-800/50 border-blue-600 text-white mt-1"
                  />
                </div>
                <div>
                  <label className="text-blue-200 text-sm">Shallow End (m)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.pool_specs.depth_shallow_end}
                    onChange={(e) => setForm({...form, pool_specs: {...form.pool_specs, depth_shallow_end: parseFloat(e.target.value) || 1.2}})}
                    className="bg-blue-800/50 border-blue-600 text-white mt-1"
                  />
                </div>
                <div>
                  <label className="text-blue-200 text-sm">Deep End (m)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.pool_specs.depth_deep_end}
                    onChange={(e) => setForm({...form, pool_specs: {...form.pool_specs, depth_deep_end: parseFloat(e.target.value) || 1.8}})}
                    className="bg-blue-800/50 border-blue-600 text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-blue-200 text-sm">Max Participants</label>
                <Input
                  type="number"
                  value={form.pool_specs.max_participants}
                  onChange={(e) => setForm({...form, pool_specs: {...form.pool_specs, max_participants: parseInt(e.target.value) || 12}})}
                  className="bg-blue-800/50 border-blue-600 text-white mt-1 w-32"
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-blue-200">Schedule</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-blue-200 text-sm">Season</label>
                  <Select 
                    value={form.season} 
                    onValueChange={(value) => setForm({...form, season: value})}
                  >
                    <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Spring">Spring</SelectItem>
                      <SelectItem value="Summer">Summer</SelectItem>
                      <SelectItem value="Autumn">Autumn</SelectItem>
                      <SelectItem value="Winter">Winter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-blue-200 text-sm">Training Date</label>
                  <Input
                    type="date"
                    value={form.training_date}
                    onChange={(e) => setForm({...form, training_date: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white mt-1"
                    data-testid="training-date-input"
                  />
                </div>
                <div>
                  <label className="text-blue-200 text-sm">Start Time</label>
                  <Input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({...form, start_time: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white mt-1"
                  />
                </div>
                <div>
                  <label className="text-blue-200 text-sm">End Time</label>
                  <Input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({...form, end_time: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-blue-200 text-sm font-semibold">Additional Notes</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Add any additional notes about this training session..."
                className="bg-blue-800/50 border-blue-600 text-white mt-1"
                rows={3}
              />
            </div>

            {/* Template Options */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={form.is_template}
                  onChange={(e) => setForm({...form, is_template: e.target.checked})}
                  className="rounded"
                />
                <span className="text-blue-200">Save as template for reuse</span>
              </label>
              {form.is_template && (
                <Input
                  value={form.template_name}
                  onChange={(e) => setForm({...form, template_name: e.target.value})}
                  placeholder="Template name"
                  className="bg-blue-800/50 border-blue-600 text-white w-64"
                />
              )}
            </div>

            {/* Participant Selection */}
            <div className="p-4 bg-teal-900/30 rounded-lg border border-teal-600/30">
              <label className="text-teal-200 text-sm font-semibold flex items-center mb-3">
                <Users2 className="mr-2" size={18} />
                {translate('participants', 'Participants')}
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {(form.participants || []).map(pid => (
                  <Badge key={pid} className="bg-teal-700/60 text-teal-100 flex items-center gap-1 px-2 py-1">
                    {getFreediverName(pid)}
                    <button onClick={() => setForm({...form, participants: form.participants.filter(p => p !== pid)})} className="ml-1 hover:text-red-300"><X size={12} /></button>
                  </Badge>
                ))}
                {(!form.participants || form.participants.length === 0) && (
                  <p className="text-teal-400 text-sm italic">{translate('noParticipantsSelected', 'No participants selected')}</p>
                )}
              </div>
              <Select onValueChange={(fid) => {
                if (fid && !(form.participants || []).includes(fid)) {
                  setForm({...form, participants: [...(form.participants || []), fid]});
                }
              }}>
                <SelectTrigger className="bg-teal-800/50 border-teal-600 text-white w-64">
                  <SelectValue placeholder={translate('addParticipant', 'Add Participant')} />
                </SelectTrigger>
                <SelectContent className="bg-teal-800 border-teal-600">
                  {freedivers.filter(f => !(form.participants || []).includes(f.id)).map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.first_name} {f.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Exercise Blocks Management */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold text-blue-200">Exercise Blocks</h4>
                <Button
                  onClick={() => {
                    const newBlock = {
                      id: `block-${Date.now()}`,
                      name: `Block ${form.exercise_blocks.length + 1}`,
                      exercises: [],
                      total_duration_minutes: 15,
                      notes: ''
                    };
                    setForm({...form, exercise_blocks: [...form.exercise_blocks, newBlock]});
                  }}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="mr-1" size={14} />
                  Add Block
                </Button>
              </div>
              {form.exercise_blocks && form.exercise_blocks.length > 0 ? (
                <div className="space-y-3">
                  {form.exercise_blocks.map((block, blockIndex) => (
                    <div key={blockIndex} className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 p-4 rounded-lg border border-purple-500/30 shadow-lg">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 space-y-3">
                          <Input
                            value={block.name}
                            onChange={(e) => {
                              const updatedBlocks = [...form.exercise_blocks];
                              updatedBlocks[blockIndex].name = e.target.value;
                              setForm({...form, exercise_blocks: updatedBlocks});
                            }}
                            placeholder="Block name"
                            className="bg-purple-800/50 border-purple-500 text-white font-semibold"
                          />
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number"
                              value={block.total_duration_minutes}
                              onChange={(e) => {
                                const updatedBlocks = [...form.exercise_blocks];
                                updatedBlocks[blockIndex].total_duration_minutes = parseInt(e.target.value) || 0;
                                setForm({...form, exercise_blocks: updatedBlocks});
                              }}
                              placeholder="Duration"
                              className="bg-purple-800/50 border-purple-500 text-white w-32"
                            />
                            <span className="text-purple-200 text-sm font-medium">minutes</span>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 ml-3">
                          <Button
                            onClick={() => {
                              if (blockIndex === 0) return;
                              const updatedBlocks = [...form.exercise_blocks];
                              [updatedBlocks[blockIndex-1], updatedBlocks[blockIndex]] = [updatedBlocks[blockIndex], updatedBlocks[blockIndex-1]];
                              setForm({...form, exercise_blocks: updatedBlocks});
                            }}
                            disabled={blockIndex === 0}
                            size="sm"
                            variant="outline"
                            className="border-purple-500 text-purple-200 hover:bg-purple-700"
                          >
                            <ArrowUp size={14} />
                          </Button>
                          <Button
                            onClick={() => {
                              if (blockIndex === form.exercise_blocks.length - 1) return;
                              const updatedBlocks = [...form.exercise_blocks];
                              [updatedBlocks[blockIndex], updatedBlocks[blockIndex+1]] = [updatedBlocks[blockIndex+1], updatedBlocks[blockIndex]];
                              setForm({...form, exercise_blocks: updatedBlocks});
                            }}
                            disabled={blockIndex === form.exercise_blocks.length - 1}
                            size="sm"
                            variant="outline"
                            className="border-purple-500 text-purple-200 hover:bg-purple-700"
                          >
                            <ArrowDown size={14} />
                          </Button>
                          <Button
                            onClick={() => {
                              const updatedBlocks = form.exercise_blocks.filter((_, i) => i !== blockIndex);
                              setForm({...form, exercise_blocks: updatedBlocks});
                            }}
                            size="sm"
                            variant="outline"
                            className="border-red-500 text-red-300 hover:bg-red-700"
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Exercises within block */}
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-purple-200 text-sm font-semibold">Exercises ({block.exercises?.length || 0})</span>
                          <Button
                            onClick={() => {
                              const newExercise = {
                                id: `exercise-${Date.now()}`,
                                name: 'New Exercise',
                                type: 'Warm-up',
                                description: '',
                                duration_minutes: 5,
                                repetitions: null,
                                distance_meters: null,
                                rest_seconds: null,
                                instructions: ''
                              };
                              const updatedBlocks = [...form.exercise_blocks];
                              updatedBlocks[blockIndex].exercises = [...(updatedBlocks[blockIndex].exercises || []), newExercise];
                              setForm({...form, exercise_blocks: updatedBlocks});
                            }}
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Plus className="mr-1" size={12} />
                            Add Exercise
                          </Button>
                        </div>
                        {block.exercises && block.exercises.length > 0 ? (
                          <div className="space-y-2">
                            {block.exercises.map((exercise, exIndex) => (
                              <div key={exIndex} className="bg-gradient-to-r from-teal-900/50 to-cyan-900/50 p-3 rounded border border-teal-500/30">
                                <div className="space-y-2">
                                  <div className="flex justify-between items-start">
                                    <Input
                                      value={exercise.name}
                                      onChange={(e) => {
                                        const updatedBlocks = [...form.exercise_blocks];
                                        updatedBlocks[blockIndex].exercises[exIndex].name = e.target.value;
                                        setForm({...form, exercise_blocks: updatedBlocks});
                                      }}
                                      placeholder="Exercise name"
                                      className="bg-teal-800/50 border-teal-500 text-white text-sm flex-1"
                                    />
                                    <Button
                                      onClick={() => {
                                        const updatedBlocks = [...form.exercise_blocks];
                                        updatedBlocks[blockIndex].exercises = updatedBlocks[blockIndex].exercises.filter((_, i) => i !== exIndex);
                                        setForm({...form, exercise_blocks: updatedBlocks});
                                      }}
                                      size="sm"
                                      variant="outline"
                                      className="border-red-500 text-red-300 hover:bg-red-700 ml-2"
                                    >
                                      <Trash size={12} />
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <Select
                                      value={exercise.type}
                                      onValueChange={(value) => {
                                        const updatedBlocks = [...form.exercise_blocks];
                                        updatedBlocks[blockIndex].exercises[exIndex].type = value;
                                        setForm({...form, exercise_blocks: updatedBlocks});
                                      }}
                                    >
                                      <SelectTrigger className="bg-teal-800/50 border-teal-500 text-white text-xs h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Warm-up">Warm-up</SelectItem>
                                        <SelectItem value="Breath Hold">Breath Hold</SelectItem>
                                        <SelectItem value="Dynamic">Dynamic</SelectItem>
                                        <SelectItem value="Static">Static</SelectItem>
                                        <SelectItem value="Recovery">Recovery</SelectItem>
                                        <SelectItem value="Cool Down">Cool Down</SelectItem>
                                        <SelectItem value="Technique">Technique</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <div className="flex items-center space-x-1">
                                      <Input
                                        type="number"
                                        value={exercise.duration_minutes || ''}
                                        onChange={(e) => {
                                          const updatedBlocks = [...form.exercise_blocks];
                                          updatedBlocks[blockIndex].exercises[exIndex].duration_minutes = parseInt(e.target.value) || null;
                                          setForm({...form, exercise_blocks: updatedBlocks});
                                        }}
                                        placeholder="Duration"
                                        className="bg-teal-800/50 border-teal-500 text-white text-xs h-8"
                                      />
                                      <span className="text-teal-200 text-xs">min</span>
                                    </div>
                                  </div>
                                  <Textarea
                                    value={exercise.description}
                                    onChange={(e) => {
                                      const updatedBlocks = [...form.exercise_blocks];
                                      updatedBlocks[blockIndex].exercises[exIndex].description = e.target.value;
                                      setForm({...form, exercise_blocks: updatedBlocks});
                                    }}
                                    placeholder="Description"
                                    className="bg-teal-800/50 border-teal-500 text-white text-xs"
                                    rows={2}
                                  />
                                  <div className="grid grid-cols-3 gap-2">
                                    <Input
                                      type="number"
                                      value={exercise.repetitions || ''}
                                      onChange={(e) => {
                                        const updatedBlocks = [...form.exercise_blocks];
                                        updatedBlocks[blockIndex].exercises[exIndex].repetitions = parseInt(e.target.value) || null;
                                        setForm({...form, exercise_blocks: updatedBlocks});
                                      }}
                                      placeholder="Reps"
                                      className="bg-teal-800/50 border-teal-500 text-white text-xs h-8"
                                    />
                                    <Input
                                      type="number"
                                      value={exercise.distance_meters || ''}
                                      onChange={(e) => {
                                        const updatedBlocks = [...form.exercise_blocks];
                                        updatedBlocks[blockIndex].exercises[exIndex].distance_meters = parseInt(e.target.value) || null;
                                        setForm({...form, exercise_blocks: updatedBlocks});
                                      }}
                                      placeholder="Distance (m)"
                                      className="bg-teal-800/50 border-teal-500 text-white text-xs h-8"
                                    />
                                    <Input
                                      type="number"
                                      value={exercise.rest_seconds || ''}
                                      onChange={(e) => {
                                        const updatedBlocks = [...form.exercise_blocks];
                                        updatedBlocks[blockIndex].exercises[exIndex].rest_seconds = parseInt(e.target.value) || null;
                                        setForm({...form, exercise_blocks: updatedBlocks});
                                      }}
                                      placeholder="Rest (s)"
                                      className="bg-teal-800/50 border-teal-500 text-white text-xs h-8"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-purple-300 text-xs italic text-center py-2">No exercises in this block</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-blue-400 text-sm italic">No exercise blocks defined. Add blocks above or select a template.</p>
              )}
            </div>

            {/* Document Attachments Section */}
            <div className="p-4 bg-blue-900/40 rounded-lg border border-blue-600/50">
              <label className="text-blue-200 text-sm font-semibold flex items-center mb-3">
                <FileText className="mr-2" size={18} />
                {translate('documentAttachments', 'Document Attachments')}
              </label>
              
              {form.attachments && form.attachments.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {form.attachments.map((attachment, index) => (
                    <div 
                      key={attachment.id || index} 
                      className="flex items-center justify-between p-3 bg-blue-800/40 rounded-lg border border-blue-700/30"
                    >
                      <div className="flex items-center space-x-3">
                        {attachment.file_type?.includes('spreadsheet') || attachment.file_type?.includes('excel') ? (
                          <div className="w-8 h-8 rounded bg-green-600 flex items-center justify-center text-white text-xs font-bold">XLS</div>
                        ) : attachment.file_type?.includes('word') || attachment.file_type?.includes('document') ? (
                          <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">DOC</div>
                        ) : (
                          <div className="w-8 h-8 rounded bg-red-600 flex items-center justify-center text-white text-xs font-bold">PDF</div>
                        )}
                        <div>
                          <p className="text-blue-200 text-sm font-medium">{attachment.filename}</p>
                          <p className="text-blue-400 text-xs">
                            {(attachment.file_size / 1024).toFixed(1)} KB • {formatDate(attachment.upload_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = attachment.file_url;
                            link.download = attachment.filename;
                            link.click();
                          }}
                          data-testid={`download-attachment-${index}`}
                        >
                          <Download size={14} />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-300 hover:bg-red-700/50"
                          onClick={() => removeAttachment(attachment.id)}
                          data-testid={`remove-attachment-${index}`}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-blue-400 text-sm italic mb-4">{translate('noAttachments', 'No attachments')}</p>
              )}
              
              <div>
                <input
                  type="file"
                  accept=".xlsx,.xls,.docx,.doc,.pdf"
                  multiple
                  onChange={handleAttachmentUpload}
                  ref={attachmentInputRef}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => attachmentInputRef.current?.click()}
                  className="bg-blue-800/50 border-blue-600 text-blue-200 hover:bg-blue-700/50"
                  data-testid="upload-attachment-btn"
                >
                  <Paperclip size={16} className="mr-2" />
                  {translate('attachFiles', 'Attach Files')}
                </Button>
                <p className="text-xs text-blue-400 mt-1">{translate('allowedFileTypes', 'Allowed: .xlsx, .xls, .docx, .doc, .pdf')}</p>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex space-x-4">
              <Button 
                onClick={editing ? updateSession : createSession} 
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-training-session-btn"
              >
                {editing ? translate('updateSession', 'Update Session') : translate('createSession', 'Create Session')}
              </Button>
              <Button 
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                  setSelectedTemplate('');
                  resetForm();
                }} 
                variant="outline"
                className="border-blue-600 text-blue-200 hover:bg-blue-700"
                data-testid="cancel-training-session-btn"
              >
                {translate('cancel', 'Cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Training Sessions List/Calendar View */}
      {viewMode === 'list' ? (
        <div className="space-y-4">
          {trainingSessions.length === 0 ? (
            <Card className="bg-gradient-to-br from-blue-800/30 to-teal-800/30 border-blue-700/20 backdrop-blur-sm">
              <CardContent className="p-8 text-center">
                <Waves size={64} className="mx-auto mb-4 text-blue-300" />
                <h3 className="text-xl font-semibold text-blue-200 mb-2">No Training Sessions</h3>
                <p className="text-blue-300 mb-4">
                  Get started by creating your first training session.
                </p>
              </CardContent>
            </Card>
          ) : (
            trainingSessions.map((session) => (
              <Card key={session.id} className="bg-gradient-to-br from-blue-800/40 to-purple-800/40 border-blue-700/30 backdrop-blur-sm">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {inlineEditing === session.id ? (
                        /* Inline Edit Mode */
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Waves className="text-blue-300" size={20} />
                            <Input value={inlineForm.name} onChange={(e) => setInlineForm({...inlineForm, name: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white h-8 text-lg font-semibold" data-testid={`inline-name-${session.id}`} />
                            <Select value={inlineForm.discipline} onValueChange={(v) => setInlineForm({...inlineForm, discipline: v})}>
                              <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white h-8 w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="STA">STA</SelectItem>
                                <SelectItem value="DYN">DYN</SelectItem>
                                <SelectItem value="DYNBF">DYNBF</SelectItem>
                                <SelectItem value="MIX">MIX</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input type="date" value={inlineForm.training_date} onChange={(e) => setInlineForm({...inlineForm, training_date: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white h-8 w-40" />
                            <Input type="time" value={inlineForm.start_time} onChange={(e) => setInlineForm({...inlineForm, start_time: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white h-8 w-28" />
                            <span className="text-blue-400">-</span>
                            <Input type="time" value={inlineForm.end_time} onChange={(e) => setInlineForm({...inlineForm, end_time: e.target.value})} className="bg-blue-800/50 border-blue-600 text-white h-8 w-28" />
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" onClick={() => saveInlineEdit(session.id)} data-testid={`inline-save-${session.id}`}><Check size={14} /></Button>
                            <Button size="sm" variant="ghost" className="text-blue-300 hover:text-white h-8" onClick={cancelInlineEdit}><X size={14} /></Button>
                          </div>
                        </div>
                      ) : (
                        /* Normal View Mode */
                        <>
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-blue-200 flex items-center cursor-pointer hover:text-blue-100" onDoubleClick={() => startInlineEdit(session)} title={translate('doubleClickToEdit', 'Double-click to quick edit')}>
                              <Waves className="mr-2" size={20} />
                              {session.name}
                            </CardTitle>
                            <Badge className="bg-blue-600">{session.discipline}</Badge>
                            {/* Capacity indicator badge */}
                            {(() => {
                              const capacity = getCapacityStatus(session);
                              const currentParticipants = session.participants?.length || 0;
                              const maxParticipants = session.pool_specs?.max_participants || 0;
                              
                              if (maxParticipants > 0) {
                                const badgeColorClass = capacity.status === 'full' 
                                  ? 'bg-red-600 text-white animate-pulse' 
                                  : capacity.status === 'nearFull' 
                                    ? 'bg-orange-600 text-white' 
                                    : capacity.status === 'filling'
                                      ? 'bg-yellow-600/80 text-yellow-100'
                                      : 'bg-teal-600/80 text-teal-100';
                                
                                return (
                                  <Badge 
                                    className={`${badgeColorClass} cursor-pointer`} 
                                    onClick={() => setShowParticipantDialog(session.id)} 
                                    data-testid={`capacity-badge-${session.id}`}
                                    title={`${capacity.percentage}% ${capacity.label}`}
                                  >
                                    <Users2 className="mr-1" size={12} />
                                    {currentParticipants}/{maxParticipants}
                                    {capacity.status === 'full' && (
                                      <span className="ml-1 text-xs">({translate('full', 'Full')})</span>
                                    )}
                                    {capacity.status === 'nearFull' && (
                                      <span className="ml-1 text-xs">({translate('nearFull', 'Near Full')})</span>
                                    )}
                                  </Badge>
                                );
                              } else if (currentParticipants > 0) {
                                return (
                                  <Badge className="bg-teal-600/80 text-teal-100 cursor-pointer" onClick={() => setShowParticipantDialog(session.id)} data-testid={`participants-badge-${session.id}`}>
                                    <Users2 className="mr-1" size={12} />
                                    {currentParticipants} {translate('participants', 'participants')}
                                  </Badge>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-blue-300 mt-1">
                            <span className="flex items-center">
                              <Clock3 className="mr-1" size={14} />
                              {formatDate(session.training_date, language)} {session.start_time} - {session.end_time}
                            </span>
                            {/* Capacity progress bar */}
                            {(() => {
                              const capacity = getCapacityStatus(session);
                              const maxParticipants = session.pool_specs?.max_participants || 0;
                              if (maxParticipants === 0) return null;
                              
                              const barColorClass = capacity.status === 'full' 
                                ? 'bg-red-500' 
                                : capacity.status === 'nearFull' 
                                  ? 'bg-orange-500' 
                                  : capacity.status === 'filling'
                                    ? 'bg-yellow-500'
                                    : 'bg-teal-500';
                              
                              return (
                                <span className="flex items-center gap-2" data-testid={`capacity-bar-${session.id}`}>
                                  <Users2 className="mr-1" size={14} />
                                  <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${barColorClass} transition-all duration-300`}
                                      style={{ width: `${Math.min(capacity.percentage, 100)}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs ${
                                    capacity.status === 'full' ? 'text-red-400' : 
                                    capacity.status === 'nearFull' ? 'text-orange-400' : ''
                                  }`}>
                                    {capacity.percentage}%
                                  </span>
                                </span>
                              );
                            })()}
                            {session.coach_name && (
                              <span className="flex items-center text-teal-300">
                                <Users2 className="mr-1" size={14} />
                                {session.coach_name}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {inlineEditing !== session.id && (
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => startInlineEdit(session)}
                          size="sm"
                          variant="ghost"
                          className="text-yellow-300 hover:text-yellow-100 hover:bg-yellow-700/30"
                          title={translate('quickEdit', 'Quick Edit')}
                          data-testid={`inline-edit-${session.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          onClick={() => setShowParticipantDialog(session.id)}
                          size="sm"
                          variant="ghost"
                          className="text-teal-300 hover:text-teal-100 hover:bg-teal-700/30"
                          title={translate('manageParticipants', 'Manage Participants')}
                          data-testid={`participants-btn-${session.id}`}
                        >
                          <UserPlus size={14} />
                        </Button>
                        <Button
                          onClick={() => openAttendanceDialog(session.id)}
                          size="sm"
                          variant="ghost"
                          className="text-indigo-300 hover:text-indigo-100 hover:bg-indigo-700/30"
                          title={translate('attendance', 'Attendance')}
                          data-testid={`attendance-btn-${session.id}`}
                        >
                          <ClipboardCheck size={14} />
                        </Button>
                        <Button
                          onClick={() => toggleExpanded(session.id)}
                          size="sm"
                          variant="ghost"
                          className="text-blue-300 hover:text-blue-100 hover:bg-blue-700/50"
                          data-testid={`toggle-details-${session.id}`}
                        >
                          {expandedDetails.has(session.id) ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                          <span className="ml-1 text-xs">{translate('details', 'Details')}</span>
                        </Button>
                        <Button
                          onClick={() => openEdit(session)}
                          size="sm"
                          variant="outline"
                          className="border-blue-600 text-blue-200"
                          data-testid={`edit-session-${session.id}`}
                        >
                          <Pencil className="mr-1" size={14} />
                          {translate('edit', 'Edit')}
                        </Button>
                        <Button
                          onClick={() => cloneSession(session.id, new Date(), `${session.name} (Copy)`)}
                          size="sm"
                          variant="outline"
                          className="border-blue-600 text-blue-200"
                          data-testid={`clone-session-${session.id}`}
                        >
                          <Copy className="mr-1" size={14} />
                          {translate('clone', 'Clone')}
                        </Button>
                        <Button
                          onClick={() => {
                            const pdfUrl = `${API_BASE_URL}/api/training-sessions/${session.id}/pdf`;
                            console.log('PDF download clicked, navigating to:', pdfUrl);
                            window.location.href = pdfUrl;
                          }}
                          size="sm"
                          variant="outline"
                          className="border-teal-600 text-teal-200 hover:bg-teal-700"
                          data-testid={`pdf-session-${session.id}`}
                        >
                          <FileText className="mr-1" size={14} />
                          PDF
                        </Button>
                        {confirmingDeleteId === session.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              onClick={() => deleteSession(session.id)}
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`confirm-delete-${session.id}`}
                            >
                              <Check className="mr-1" size={14} />
                              {translate('confirm', 'Confirm')}
                            </Button>
                            <Button
                              onClick={() => setConfirmingDeleteId(null)}
                              size="sm"
                              variant="outline"
                              className="border-blue-600 text-blue-300 hover:bg-blue-700"
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            onClick={() => setConfirmingDeleteId(session.id)}
                            size="sm"
                            variant="outline"
                            className="border-red-600 text-red-300 hover:bg-red-700"
                            data-testid={`delete-session-${session.id}`}
                          >
                            <Trash className="mr-1" size={14} />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                {/* Collapsible Details Section */}
                {expandedDetails.has(session.id) && (
                  <CardContent className="pt-2 border-t border-blue-700/30">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="text-sm font-semibold text-blue-200 mb-1">{translate('location', 'Location')}</h5>
                        <div className="text-blue-300 text-sm">
                          <p>{session.location?.building_name}</p>
                          <p>{session.location?.organization}</p>
                          <p className="flex items-center">
                            <MapPin className="mr-1" size={12} />
                            {session.location?.city}, {session.location?.country}
                          </p>
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-semibold text-blue-200 mb-1">{translate('poolInfo', 'Pool Info')}</h5>
                        <div className="text-blue-300 text-sm">
                          <p>{session.pool_specs?.type} pool • {session.pool_specs?.number_of_lanes} {translate('lanes', 'lanes')}</p>
                          <p>{translate('depth', 'Depth')}: {session.pool_specs?.depth_shallow_end}m - {session.pool_specs?.depth_deep_end}m</p>
                        </div>
                      </div>
                    </div>
                    
                    {session.notes && (
                      <div className="mt-4">
                        <h5 className="text-sm font-semibold text-blue-200 mb-1">{translate('notes', 'Notes')}</h5>
                        <p className="text-blue-300 text-sm">{session.notes}</p>
                      </div>
                    )}
                    
                    {/* Participants in Details */}
                    {session.participants && session.participants.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-semibold text-teal-200 mb-2 flex items-center">
                          <Users2 className="mr-1" size={14} />
                          {translate('participants', 'Participants')} ({session.participants.length})
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {session.participants.map(pid => (
                            <Badge key={pid} className="bg-teal-700/50 text-teal-100">{getFreediverName(pid)}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
                
                {/* Exercise Blocks Section - Always visible if present */}
                {session.exercise_blocks && session.exercise_blocks.length > 0 && (
                  <CardContent className={expandedDetails.has(session.id) ? "pt-0" : "pt-2"}>
                    <div className="border-t border-purple-500/30 pt-3">
                      <h5 className="text-sm font-semibold text-purple-200 mb-2">{translate('trainingProgram', 'Training Program')}</h5>
                      <Accordion type="single" collapsible className="space-y-2">
                        {session.exercise_blocks.map((block, blockIndex) => (
                          <AccordionItem key={blockIndex} value={`block-${blockIndex}`} className="border border-purple-500/40 rounded-lg bg-gradient-to-r from-purple-900/30 to-indigo-900/30 shadow-md">
                            <AccordionTrigger className="px-4 py-2 hover:no-underline hover:bg-purple-800/40">
                              <div className="flex justify-between items-center w-full pr-2">
                                <span className="text-purple-100 font-semibold">{block.name}</span>
                                <span className="text-purple-300 text-sm font-medium">{block.total_duration_minutes} {translate('minutes', 'minutes')}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-3">
                              {block.exercises && block.exercises.length > 0 ? (
                                <div className="space-y-3">
                                  {block.exercises.map((exercise, exIndex) => (
                                    <div key={exIndex} className="bg-gradient-to-r from-teal-900/40 to-cyan-900/40 p-3 rounded border border-teal-500/40 shadow">
                                      <div className="flex justify-between items-start mb-2">
                                        <h6 className="text-teal-100 font-medium">{exercise.name}</h6>
                                        <Badge className="bg-teal-600 text-xs">{exercise.type}</Badge>
                                      </div>
                                      <p className="text-teal-200 text-sm mb-2">{exercise.description}</p>
                                      <div className="grid grid-cols-2 gap-2 text-xs text-teal-300">
                                        {exercise.duration_minutes && <span>Duration: {exercise.duration_minutes} min</span>}
                                        {exercise.repetitions && <span>Reps: {exercise.repetitions}</span>}
                                        {exercise.distance_meters && <span>Distance: {exercise.distance_meters}m</span>}
                                        {exercise.rest_seconds && <span>Rest: {exercise.rest_seconds}s</span>}
                                      </div>
                                      {exercise.instructions && (
                                        <p className="text-teal-300 text-xs mt-2 italic">{exercise.instructions}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-purple-400 text-sm italic">No exercises defined</p>
                              )}
                              {block.notes && (
                                <p className="text-purple-300 text-sm mt-3 italic">Note: {block.notes}</p>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  </CardContent>
                )}
                  
                {/* Attachments Section */}
                {session.attachments && session.attachments.length > 0 && (
                  <CardContent className="pt-0">
                    <div className="mt-4 border-t border-blue-500/30 pt-4">
                      <h5 className="text-sm font-semibold text-blue-200 mb-2 flex items-center">
                        <Paperclip className="mr-1" size={14} />
                        {translate('attachedFiles', 'Attached Files')} ({session.attachments.length})
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {session.attachments.map((attachment, index) => (
                          <div 
                            key={attachment.id || index}
                            className="flex items-center space-x-2 p-2 bg-blue-800/40 rounded border border-blue-700/30"
                          >
                            {attachment.file_type?.includes('spreadsheet') || attachment.file_type?.includes('excel') ? (
                              <div className="w-6 h-6 rounded bg-green-600 flex items-center justify-center text-white text-xs font-bold">XLS</div>
                            ) : attachment.file_type?.includes('word') || attachment.file_type?.includes('document') ? (
                              <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">DOC</div>
                            ) : (
                              <div className="w-6 h-6 rounded bg-red-600 flex items-center justify-center text-white text-xs font-bold">PDF</div>
                            )}
                            <span className="text-blue-200 text-sm">{attachment.filename}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-blue-300 hover:text-white"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = attachment.file_url;
                                link.download = attachment.filename;
                                link.click();
                              }}
                            >
                              <Download size={12} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      ) : (
        /* Calendar View Implementation */
        <Card className="bg-gradient-to-br from-blue-800/30 to-teal-800/30 border-blue-700/20 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Calendar */}
              <div className="lg:w-1/3">
                <div className="bg-blue-900/50 rounded-lg p-4">
                  <h4 className="text-blue-200 font-semibold mb-4 text-center">{translate('selectDate', 'Select Date')}</h4>
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-between w-full mb-4">
                      <button
                        onClick={() => navigateMonth(-1)}
                        className="p-2 rounded-lg bg-blue-800/50 border border-blue-600 text-blue-200 hover:bg-blue-700 hover:text-white transition-colors"
                        data-testid="month-prev-btn"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="text-white font-medium text-sm capitalize">{getMonthLabel()}</span>
                      <button
                        onClick={() => navigateMonth(1)}
                        className="p-2 rounded-lg bg-blue-800/50 border border-blue-600 text-blue-200 hover:bg-blue-700 hover:text-white transition-colors"
                        data-testid="month-next-btn"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 w-full">
                      {(language === 'fr' 
                        ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
                        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
                      ).map(day => (
                        <div key={day} className="text-center text-xs text-blue-400 font-semibold py-1">
                          {day}
                        </div>
                      ))}
                      {(() => {
                        const month = calendarMonth || new Date().toISOString().slice(0, 7);
                        const [year, monthNum] = month.split('-').map(Number);
                        const firstDay = new Date(year, monthNum - 1, 1).getDay();
                        const daysInMonth = new Date(year, monthNum, 0).getDate();
                        const days = [];
                        
                        for (let i = 0; i < firstDay; i++) {
                          days.push(<div key={`empty-${i}`} className="h-8"></div>);
                        }
                        
                        for (let day = 1; day <= daysInMonth; day++) {
                          const dateStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const hasSession = trainingSessions.some(s => s.training_date?.split('T')[0] === dateStr);
                          const isSelected = selectedCalendarDate === dateStr;
                          
                          days.push(
                            <button
                              key={day}
                              onClick={() => setSelectedCalendarDate(dateStr)}
                              className={`h-8 w-8 rounded-full text-sm flex items-center justify-center transition-all ${
                                isSelected 
                                  ? 'bg-teal-500 text-white' 
                                  : hasSession 
                                    ? 'bg-blue-600 text-white hover:bg-blue-500' 
                                    : 'text-blue-200 hover:bg-blue-700/50'
                              }`}
                            >
                              {day}
                              {hasSession && !isSelected && (
                                <span className="absolute bottom-0 w-1 h-1 bg-teal-400 rounded-full"></span>
                              )}
                            </button>
                          );
                        }
                        
                        return days;
                      })()}
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-blue-400 text-center">
                    <span className="inline-flex items-center mr-4"><span className="w-3 h-3 bg-blue-600 rounded-full mr-1"></span> {translate('hasSession', 'Has Session')}</span>
                    <span className="inline-flex items-center"><span className="w-3 h-3 bg-teal-500 rounded-full mr-1"></span> {translate('selected', 'Selected')}</span>
                  </div>
                </div>
              </div>
              
              {/* Sessions for selected date */}
              <div className="lg:w-2/3">
                <h4 className="text-blue-200 font-semibold mb-4">
                  {selectedCalendarDate 
                    ? `${translate('sessionsFor', 'Sessions for')} ${new Date(selectedCalendarDate + 'T00:00:00').toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`
                    : (translate('selectDateToViewSessions', 'Select a date to view sessions'))
                  }
                </h4>
                
                {selectedCalendarDate ? (
                  <div className="space-y-3">
                    {trainingSessions
                      .filter(s => s.training_date?.split('T')[0] === selectedCalendarDate)
                      .length === 0 ? (
                        <div className="text-center py-8 text-blue-300">
                          <CalendarDays size={48} className="mx-auto mb-2 opacity-50" />
                          <p>{translate('noSessionsOnDate', 'No training sessions on this date')}</p>
                          <Button 
                            onClick={() => {
                              setForm(prev => ({...prev, training_date: selectedCalendarDate}));
                              setShowForm(true);
                            }}
                            className="mt-3 bg-teal-600 hover:bg-teal-700"
                            size="sm"
                          >
                            <Plus size={14} className="mr-1" />
                            {translate('createSession', 'Create Session')}
                          </Button>
                        </div>
                      ) : (
                        trainingSessions
                          .filter(s => s.training_date?.split('T')[0] === selectedCalendarDate)
                          .map(session => (
                            <Card key={session.id} className="bg-blue-800/40 border-blue-700/30">
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h5 className="text-white font-semibold">{session.name}</h5>
                                    <div className="flex flex-wrap gap-2 mt-2 text-sm text-blue-300">
                                      <span className="flex items-center">
                                        <Clock3 size={14} className="mr-1" />
                                        {session.start_time} - {session.end_time}
                                      </span>
                                      <Badge className="bg-blue-600">{session.discipline}</Badge>
                                      {session.location?.building_name && (
                                        <span className="flex items-center">
                                          <MapPin size={14} className="mr-1" />
                                          {session.location.building_name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex space-x-1">
                                    <Button
                                      onClick={() => openEdit(session)}
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-blue-300 hover:bg-blue-700/50"
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                    {confirmingDeleteId === session.id ? (
                                      <div className="flex items-center gap-1">
                                        <Button
                                          onClick={() => deleteSession(session.id)}
                                          size="sm"
                                          className="bg-red-600 hover:bg-red-700 text-white h-8 px-2"
                                        >
                                          <Check size={14} />
                                        </Button>
                                        <Button
                                          onClick={() => setConfirmingDeleteId(null)}
                                          size="sm"
                                          variant="ghost"
                                          className="h-8 w-8 p-0 text-blue-300 hover:bg-blue-700/50"
                                        >
                                          <X size={14} />
                                        </Button>
                                      </div>
                                    ) : (
                                      <Button
                                        onClick={() => setConfirmingDeleteId(session.id)}
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0 text-red-300 hover:bg-red-700/50"
                                      >
                                        <Trash size={14} />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))
                      )
                    }
                  </div>
                ) : (
                  <div className="text-center py-12 text-blue-300">
                    <CalendarDays size={64} className="mx-auto mb-4 opacity-30" />
                    <p>{translate('clickDateToView', 'Click on a date to view or create training sessions')}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TrainingSessionsTab;
